import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ShieldCheck, Truck, BadgeCheck, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const pct = (r) => `${(r * 100).toFixed(r * 100 % 1 ? 1 : 0)}%`;

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/pricing").then((r) => setP(r.data)); }, []);
  useEffect(() => {
    if (!user) { setCurrent(null); return; }
    api.get("/seller/plan").then((r) => setCurrent(r.data)).catch(() => {});
  }, [user]);

  const subscribe = async (key) => {
    if (!user) { nav("/auth"); return; }
    setBusy(key);
    try {
      const { data } = await api.post("/billing/subscribe", { plan: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(null); }
  };

  if (!p) return <div className="p-16 label-eyebrow">Loading…</div>;

  return (
    <div data-testid="pricing-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-8">
        <div className="label-eyebrow">Fees & plans</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">What it costs.</h1>
        <p className="mt-4 text-base text-neutral-600 max-w-2xl leading-relaxed">
          Listing is always free and sellers keep 100% of every sale — we no longer charge seller commission.
          Buyers pay a small protection fee that funds the {p.guarantee_days}-day authenticity guarantee. Premium tools are optional.
        </p>
      </div>

      <section className="mt-10 grid lg:grid-cols-3 gap-4" data-testid="plans-grid">
        {p.plans.map((plan) => {
          const isCurrent = current && current.key === plan.key;
          const featured = plan.key === "pro";
          return (
            <div key={plan.key} data-testid={`plan-card-${plan.key}`}
              className="hairline bg-white p-7 flex flex-col"
              style={featured ? { borderColor: "#1E3A2B", borderWidth: 2 } : {}}>
              <div className="flex items-baseline justify-between">
                <div className="label-eyebrow">{plan.name}</div>
                {featured && <span className="chip" data-active="true">Most popular</span>}
                {isCurrent && <span className="chip" data-testid={`plan-current-${plan.key}`}>Your plan</span>}
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-4xl tracking-tighter">
                  {plan.price ? `$${plan.price.toFixed(2)}` : "$0"}
                </span>
                <span className="text-sm text-neutral-500 mb-1">/ month</span>
              </div>
              <div className="mt-1 text-xs text-neutral-500">No seller commission — keep 100%</div>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check size={15} className="mt-0.5 shrink-0" style={{ color: "#16A34A" }} />{f}
                  </li>
                ))}
              </ul>
              {plan.price > 0 ? (
                <button className={featured ? "btn-primary mt-7" : "btn-outline mt-7"} disabled={busy === plan.key}
                  onClick={() => subscribe(plan.key)} data-testid={`subscribe-${plan.key}`}>
                  {busy === plan.key ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
                  {isCurrent ? "Extend 30 days" : `Get ${plan.name}`}
                </button>
              ) : (
                <Link to="/sell" className="btn-outline mt-7" data-testid="plan-free-cta">Start listing free</Link>
              )}
            </div>
          );
        })}
      </section>

      <div className="mt-4 hairline p-4 text-sm flex items-start gap-2" data-testid="private-fee-line"
        style={{ borderColor: "#1E3A2B", background: "#F3F6F2", color: "#1E3A2B" }}>
        <Lock size={15} className="mt-0.5 shrink-0" />
        <span><strong>Private sales:</strong> Sellers pay a <strong>3% seller fee</strong> on every item sold from
        their private collection. Upgrade to <strong>Pro</strong> to remove this fee entirely.</span>
      </div>

      {current && (
        <div className="mt-6 hairline p-5 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="current-plan-strip">
          <span className="label-eyebrow">Your plan</span>
          <span className="font-display text-lg">{current.name}</span>
          <span className="text-sm text-neutral-600">No seller commission</span>
          <span className="text-sm text-neutral-600">
            {current.active_listings}{current.listing_limit ? ` / ${current.listing_limit}` : ""} active listings
          </span>
          {current.expires_at && (
            <span className="text-sm text-neutral-600">Renews {new Date(current.expires_at).toLocaleDateString()}</span>
          )}
        </div>
      )}

      <section className="mt-16 grid md:grid-cols-3 gap-6">
        <div className="hairline p-6" style={{ borderColor: "#2563EB" }}>
          <div className="flex items-center gap-2">
            <ShieldCheck size={15} style={{ color: "#2563EB" }} />
            <span className="label-eyebrow" style={{ color: "#2563EB" }}>Buyer protection fee</span>
          </div>
          <div className="mt-3 font-display text-2xl">
            {pct(p.buyer_protection.rate)} + ${p.buyer_protection.fixed.toFixed(2)}
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            A flat {pct(p.buyer_protection.rate)} of the item price plus a fixed ${p.buyer_protection.fixed.toFixed(2)} per
            order, charged to the buyer at checkout. It funds the {p.guarantee_days}-day authenticity guarantee, the
            payout hold and AI claim reviews.
          </p>
          <div className="mt-3 text-sm flex justify-between" data-testid="protection-flat">
            <span className="text-neutral-500">Every order</span>
            <span>{pct(p.buyer_protection.rate)} + ${p.buyer_protection.fixed.toFixed(2)}</span>
          </div>
        </div>

        <div className="hairline p-6" style={{ borderColor: "#16A34A" }}>
          <div className="flex items-center gap-2">
            <BadgeCheck size={15} style={{ color: "#16A34A" }} />
            <span className="label-eyebrow" style={{ color: "#16A34A" }}>Designer authentication</span>
          </div>
          <p className="mt-3 text-sm text-neutral-600">
            Branded items over ${p.authentication_required_above.toFixed(0)} get a deeper authentication pass. The fee
            is deducted from the seller's payout — never charged upfront, never charged if the item doesn't sell.
          </p>
          <ul className="mt-3 text-sm space-y-1" data-testid="auth-fee-tiers">
            {p.authentication_fees.map((t, i) => (
              <li key={`${t.from}-${t.to ?? "up"}`} className="flex justify-between">
                <span className="text-neutral-500">
                  ${t.from.toFixed(0)}{t.to ? ` – $${(t.to - 0.01).toFixed(0)}` : "+"}
                </span>
                <span>${t.fee.toFixed(2)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-neutral-500">
            Disclosed replicas are capped at ${p.replica_price_cap.toFixed(0)}.
          </p>
        </div>

        <div className="hairline p-6">
          <div className="flex items-center gap-2">
            <Truck size={15} />
            <span className="label-eyebrow">Shipping</span>
          </div>
          <p className="mt-3 text-sm text-neutral-600">
            Negotiated flat rates at checkout — no printer maths, no surprise postage.
          </p>
          <ul className="mt-3 text-sm space-y-1" data-testid="shipping-rates">
            {p.shipping.map((s) => (
              <li key={s.key} className="flex justify-between">
                <span className="text-neutral-500">{s.label} · {s.eta}</span>
                <span>{s.price ? `$${s.price.toFixed(2)}` : "Free"}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="mt-14 hairline p-8 text-center">
        <div className="label-eyebrow">Stores & resellers</div>
        <h2 className="font-display text-3xl tracking-tight mt-2">Selling at volume?</h2>
        <p className="mt-3 text-sm text-neutral-600 max-w-2xl mx-auto">
          The Store plan gives you a public storefront link buyers can browse, CSV export of
          every listing and sale, and top placement in Browse.
        </p>
        <button className="btn-primary mt-6" onClick={() => subscribe("store")} data-testid="cta-store-plan">
          Get the Store plan
        </button>
      </div>
    </div>
  );
}
